import React, { useCallback, useEffect, useState } from 'react';
import { getIntegration, getMyCredential } from './apiIntegrationService';
import { Endpoint, IntegrationDetail } from './apiIntegrations.types';
import './apiintegrations.css';

// Read-only view of what an API Integration calls - the integration
// counterpart of MyToolsPage's McpDetailBody. Shown in Browse, My Creations
// and Installed detail. Never shows the credential, only which kind is used.

const AUTH_LABELS: Record<string, string> = {
  none: 'No authentication',
  api_key: 'API key',
  bearer: 'Bearer token',
};

// Long APIs (Pokémon has 54 endpoints) start folded to this many rows.
const FOLDED_ENDPOINTS = 8;

export function methodClass(method: string): string {
  return `ai-method ai-method--${method.toLowerCase()}`;
}

function hostOf(url?: string | null): string {
  if (!url) return 'Not set';
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

export function authSummary(detail: IntegrationDetail['integration']): string {
  const label = AUTH_LABELS[detail.auth_type] ?? detail.auth_type;
  if (detail.auth_type === 'api_key' && detail.auth_param_name) {
    return `${label} in the ${detail.auth_location === 'query' ? 'query string' : 'header'} (${detail.auth_param_name})`;
  }
  return label;
}

function keyRule(integration: IntegrationDetail['integration']): string {
  return integration.installer_key_scope === 'per_user'
    ? 'Each person who installs it enters their own'
    : 'One per agency, entered by an Agency Admin';
}

const EndpointRow: React.FC<{ endpoint: Endpoint }> = ({ endpoint }) => (
  <tr>
    <td className="ai-endpoint-table__endpoint">
      <span className={methodClass(endpoint.http_method)}>{endpoint.http_method}</span>
      <span className="ai-mono ai-endpoint-table__path">{endpoint.path_template}</span>
    </td>
    <td>
      <div className="ai-endpoint-table__what">{endpoint.summary || endpoint.description}</div>
      {endpoint.returns_summary && (
        <div className="ai-endpoint-table__note">
          <span className="ai-endpoint-table__label">Returns</span> <span className="ai-mono">{endpoint.returns_summary}</span>
        </div>
      )}
      {endpoint.body_format === 'multipart' && (
        <div className="ai-endpoint-table__note ai-warn-text">File upload - not available to agents yet</div>
      )}
    </td>
  </tr>
);

const IntegrationDetailBody: React.FC<{ apiIntegrationId?: number | null }> = ({ apiIntegrationId }) => {
  const [detail, setDetail] = useState<IntegrationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  // Whether the viewer's calls already have a key (their own or their agency's).
  const [keySaved, setKeySaved] = useState(false);

  const load = useCallback(() => {
    if (!apiIntegrationId) return;
    setError(null);
    setDetail(null);
    setKeySaved(false);
    getIntegration(apiIntegrationId)
      .then(setDetail)
      .catch(err => setError(err instanceof Error ? err.message : "Couldn't load this integration."));
    // Only decides whether to show the "Requires credentials" badge, so a failure just leaves it showing.
    getMyCredential(apiIntegrationId).then(status => setKeySaved(!!status.set)).catch(() => undefined);
  }, [apiIntegrationId]);

  useEffect(() => { load(); }, [load]);

  if (!apiIntegrationId) return <p className="mt-table-empty">This integration hasn't finished being set up.</p>;
  if (error) {
    return (
      <div>
        <p className="ai-error">{error}</p>
        <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={load}>Try again</button>
      </div>
    );
  }
  if (!detail) return <p className="ai-hint">Loading integration details…</p>;

  const { integration, endpoints } = detail;
  const needsKey = integration.auth_type !== 'none';
  const shown = showAll ? endpoints : endpoints.slice(0, FOLDED_ENDPOINTS);

  return (
    <div className="ai-detail">
      <div>
        <div className="mt-detail-field-label">Connection</div>
        {!needsKey && <span className="mt-badge mt-badge--status">No credentials required</span>}
        {needsKey && !keySaved && <span className="mt-badge mt-badge--warning">🔑 Requires credentials</span>}
        <dl className="ai-detail-grid">
          <dt>Calls</dt><dd className="ai-mono">{hostOf(integration.base_url)}</dd>
          <dt>Authentication</dt><dd>{authSummary(integration)}</dd>
          {needsKey && (<><dt>API key</dt><dd>{keyRule(integration)}</dd></>)}
        </dl>
      </div>

      {endpoints.length > 0 && (
        <div>
          <div className="mt-detail-field-label">
            Endpoints <span className="ai-section-count">{endpoints.length}</span>
          </div>
          <div className="ai-table-scroll">
            <table className="ai-param-table ai-endpoint-table">
              <thead><tr><th>Endpoint</th><th>What it does</th></tr></thead>
              <tbody>{shown.map(ep => <EndpointRow key={ep.id} endpoint={ep} />)}</tbody>
            </table>
          </div>
          {endpoints.length > FOLDED_ENDPOINTS && (
            <button type="button" className="ai-toggle-link" onClick={() => setShowAll(!showAll)} aria-expanded={showAll}>
              {showAll ? 'Show fewer' : `Show all ${endpoints.length} endpoints`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default IntegrationDetailBody;
